/* eslint-disable no-unreachable */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { EquipmentSeriesIncompatibleWithTheBusinessException } from 'src/app/shared/errors/product-service/product-service-errors';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { AddRemoveEquipmentRequestDTO } from 'src/app/shared/models/dtos/requests/add-remove-equipments-request-dto/add-remove-equipments-request-dto';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import {
  errorObjectBuilder,
  serialValidator,
} from 'src/app/shared/validators/registration-validators';
import { TBusinessEquipmentFormPhase } from 'src/app/shared/components/molecules/business-equipment-form/business-equipment-form.component';
import { EphemeralFeedback } from 'src/app/shared/models/ephemeral-feedback/ephemeral-feedback';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { DataUpdateFeedback } from 'src/app/shared/models/data-update-feedback/data-update-feedback';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SerialModelValidationStatus } from 'src/app/shared/models/dtos/responses/serial-model-validation-response-dto/serial-model-validation-status';
import { fromTRegisterEquipmentFormGroup } from 'src/app/shared/models/dtos/requests/equipment-sap-registrarion-request-dto/equipment-sap-registration-request-dto';
import {
  DialogModalData,
  SecondaryContent,
} from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { BusinessFrozenException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';

@Component({
  selector: 'app-customer-printers',
  templateUrl: './customer-printers.component.html',
  styleUrls: ['./customer-printers.component.scss'],
})

/**
 * Customer printer from customer details page
 */
export class CustomerPrintersComponent implements OnInit, OnDestroy {
  @Input() businessId!: number;
  @Input() viewOnly!: boolean;
  businessSummary!: BusinessSummaryResponseDTO;
  equipmentData!: BusinessEquipmentFormObject;
  businessEquipmentFormPhase: TBusinessEquipmentFormPhase = 'LNR_REGISTER';
  readonly ephemeralFeedback = new EphemeralFeedback<
    'eligible' | 'registered'
  >();
  isAddingEquipment = false;
  equipmentIsChanged = false;
  private subsink = new Subscription();
  saveEquipmentFeedback = new DataUpdateFeedback();
  equipmentActionLoading = false;
  equipmentDataLoading = false;
  readonly labels = {
    couldNotCheckPrinterSerialData: 'unable-check-printer-serial-number',
    couldNotRegisterPrinter: 'unable-register-printer',
    noCommPrinterDatabase: 'could-not-communicate-with-printer-database',
    ok: 'ok',
    doYouWishToSaveChanges: 'do-you-wish-to-save-changes',
    cancel: 'cancel',
    saveChanges: 'save-changes',
    editBusinessEquipment: 'edit-business-equipment',
    equipmentsBeingADDED: 'equipments-being-added',
    equipmentsBeingREMOVED: 'equipments-being-removed',
    somethingWrong: 'something-wrong',
    businessFrozen: 'business-is-frozen-exception',
    validate: 'validate',
    removePrinter: 'remove-printer',
    removePrinterLast: 'equipment-removal-disclaimer-1',
    removePrinterConfirmation: 'remove-printer-confirmation',
    equipmentRemoval: 'equipment-removal',
    activateBusinessInfoPrinter: 'activate-business-info-printer',
    disableBusinessWarning: 'disable-business-warning',
  };
  errorMessages = {
    cantUpdateEmptyBusinessId: 'cant-update-empty-business-id',
    repIdMustBePresent: 'representative-id-must-be-present',
    invalidForm: 'invalid-form',
  };
  readonly invalidEquipmentSeriesMessage = 'invalid-equipment-series-exception';
  originalEquipmentData!: { equipments: Array<any> };
  page = 0;
  pageSize = 25;

  newEquipmentFormGroup?: FormGroup = new FormGroup({
    serial: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(18),
      serialValidator(),
    ]),
    planModel: new UntypedFormControl('New Printer', [Validators.required]),
  });

  serverError = false;
  equipmentExistsError = false;
  isRegistration: boolean = false;
  loading = false;
  privacyPolicyLink!: string;
  termsAndConditionsLink?: string;

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private productService: ProductService,
    private translationService: TranslationService,
    private snackbarService: SnackBarService,
    public registerService: RegisterService,
    private authenticatedUserService: AuthenticatedUserService,
    private readonly dialogService: DialogService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.userService
      .getBusinessSummary(this.businessId)
      .subscribe((summary) => {
        this.businessSummary = summary;
        const registerEquipmentFormGroupControls =
          this.registerService.registerEquipmentFormGroup.controls;
        const representative = this.businessSummary.representatives?.find(
          (res) => res.admin === true
        );

        registerEquipmentFormGroupControls.representativeName.setValue(
          representative!.firstName || ''
        );
        registerEquipmentFormGroupControls.representativeLastName.setValue(
          representative!.lastName || ''
        );
        registerEquipmentFormGroupControls.country.setValue(
          this.businessSummary.country?.initials || ''
        );
        registerEquipmentFormGroupControls.stateOrRegion.setValue(
          this.businessSummary.state || ''
        );
        registerEquipmentFormGroupControls.businessEmail.setValue(
          this.businessSummary.email || ''
        );
        registerEquipmentFormGroupControls.businessName.setValue(
          this.businessSummary.name || ''
        );
        registerEquipmentFormGroupControls.primaryPhone.setValue(
          this.businessSummary.phoneNumber || ''
        );
      });
    this.translationService.translateObject(this.errorMessages);
    this.equipmentData = this.registerService.getBusinessEquipmentFormObject(
      this.page,
      this.pageSize
    );
    this.equipmentData.formGroup.disable();
    (
      this.equipmentData.formGroup.get('equipments') as UntypedFormArray
    )?.controls.forEach((control) => {
      control.disable();
    });

    this.subsink.add(
      this.equipmentData.formGroup.statusChanges.subscribe(() => {
        this.equipmentIsChanged = this.isEquipmentChanged();
      })
    );

    this.registerService
      .getTermsOfUseAndPrivacyConditionsURLs()
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.termsAndConditionsLink = res.termsOfUseURL;
          this.privacyPolicyLink = res.privacyConditionsURL;
        },
      });

    this.fetchingInitialInfo();
    this.updateEquipmentData();

    this.translationService.translateObject(this.labels);

    this.isAddingEquipment = true;
  }

  /**
   * @param {number} pagesToLoad
   * Pulls equipment data from API
   */
  async updateEquipmentData(pagesToLoad?: number) {
    this.equipmentActionLoading = true;
    this.equipmentData = this.registerService.getBusinessEquipmentFormObject(
      this.page,
      this.pageSize,
      this.businessId,
      (formGroup) => {
        this.equipmentDataLoading = false;
        this.equipmentActionLoading = false;
        this.isAddingEquipment = true;
        this.newEquipmentFormGroup?.get('serial')?.setValue('');
        this.newEquipmentFormGroup?.markAsUntouched();
        this.originalEquipmentData = formGroup.value;
        this.originalEquipmentData.equipments.forEach((equipment) => {
          delete equipment['model'];
        });
        if (pagesToLoad! > 0) {
          this.page++;
          this.updateEquipmentData(pagesToLoad! - 1);
        }
      }
    );
  }

  /**
   * Pulls equipment data from API
   */
  updatePageEquipmentData() {
    this.page++;
    this.updateEquipmentData();
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.registerService.resetForms();
  }

  /**
   * Make changes to business equipment information permanent
   * @param {AbstractControl} equipment
   */
  async saveEquipment(equipment: AbstractControl) {
    this.equipmentActionLoading = true;

    let equipmentsToAdd = [equipment.getRawValue()];
    equipmentsToAdd.map((item: any) => (item.businessId = this.businessId));

    const dto = new AddRemoveEquipmentRequestDTO(equipmentsToAdd, []);

    this.productService
      .updateEquipmentList(dto, this.businessId)
      .pipe(take(1))
      .subscribe({
        error: async (err) => {
          this.removeTuple();
          this.updateEquipmentData();
          this.equipmentActionLoading = false;
          if (
            err instanceof EquipmentSeriesIncompatibleWithTheBusinessException
          ) {
            const serial = dto.equipmentsToAdd[0].serial;
            const model = dto.equipmentsToAdd[0].planModel;

            const errorMessage = await this.translationService.translateString(
              this.invalidEquipmentSeriesMessage,
              { serial, model }
            );
            this.dialogService.openDialog({
              title: this.labels.somethingWrong,
              content: errorMessage,
            });
          } else if (err instanceof BusinessFrozenException) {
            this.snackbarService.error(this.labels.businessFrozen);
          } else {
            this.snackbarService.error(this.labels.somethingWrong);
          }
        },
        complete: () => {
          this.equipmentActionLoading = false;
          this.translationService
            .translateString('success-updating-data')
            .then((string) => this.snackbarService.success(string));
          this.refreshFullEquipmentList();
          equipmentsToAdd = [];
        },
      });
  }

  /**
   * Refreshes the full equipment list
   */
  async refreshFullEquipmentList() {
    const currentPage = this.page;
    this.page = 0;
    await this.updateEquipmentData(currentPage);

    const element = document.querySelector('.equipment-content');

    if (element) {
      element.scrollTop = 0;
    }
  }

  /**
   * Deletes equipment on delete button click
   * @param {FormGroup} control
   */
  async deleteEquipment(control: FormGroup) {
    const equipments = this.equipmentData.formGroup.get(
      'equipments'
    ) as UntypedFormArray;
    const isLastEquipment = !equipments || equipments.controls.length === 1;
    const dialogData: DialogModalData = {
      title: this.labels.equipmentRemoval,
      content: isLastEquipment
        ? this.labels.removePrinterLast
        : this.labels.removePrinterConfirmation,
      secondaryContent: isLastEquipment ? await this.getSecondaryContent() : [],
      failButtonText: this.labels.cancel,
      deleteButtonText: this.labels.removePrinter,
    };
    this.dialogService.openDialog(
      dialogData,
      () => {
        const dto = new AddRemoveEquipmentRequestDTO(
          [],
          [control.get('id')?.value]
        );

        this.equipmentDataLoading = true;

        this.productService
          .updateEquipmentList(dto, this.businessId)
          .pipe(take(1))
          .subscribe({
            error: async (error) => {
              if (error instanceof BusinessFrozenException) {
                this.snackbarService.error(this.labels.businessFrozen);
              } else {
                this.snackbarService.error(this.labels.somethingWrong);
              }
              this.equipmentDataLoading = false;
              this.equipmentActionLoading = false;
            },
            complete: () => {
              this.equipmentDataLoading = false;
              this.equipmentActionLoading = false;
              this.translationService
                .translateString('success-updating-data')
                .then((string) => this.snackbarService.success(string));
              this.refreshFullEquipmentList();
            },
          });
      },
      () => {}
    );
  }

  /**
   * @return {boolean}
   */
  isSaveEquipmentDisabled(): boolean {
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      return this.epsonUnregisteredSaveEquipmentDisabled();
    } else {
      return this.lnrRegisterSaveEquipmentDisabled();
    }
  }

  /**
   * Whether save equipment button is disabled when in epson unregistered phase
   * @return {boolean}
   */
  epsonUnregisteredSaveEquipmentDisabled(): boolean {
    return (
      this.lnrRegisterSaveEquipmentDisabled() ||
      this.isRegisterEquipmentFormGroupInvalid()
    );
  }

  /**
   * Decides if register service's register equipment form group is invalid
   * for this smaller version's purpose
   * @return {boolean}
   */
  isRegisterEquipmentFormGroupInvalid(): boolean {
    return (
      this.registerService.registerEquipmentFormGroup.controls.placeOfPurchase
        .invalid ||
      this.registerService.registerEquipmentFormGroup.controls.installationDate
        .invalid ||
      this.registerService.registerEquipmentFormGroup.controls.agree.invalid
    );
  }

  /**
   * Whether save equipment is disabled in lnr register phase
   * @return {boolean}
   */
  lnrRegisterSaveEquipmentDisabled(): boolean {
    return (
      this.equipmentDataLoading ||
      this.newEquipmentFormGroup!.invalid ||
      (this.isAddingEquipment &&
        (!this.equipmentIsChanged || !this.isEquipmentChanged())) ||
      this.equipmentActionLoading
    );
  }
  /**
   * Whether business data and equipment data are loading or not
   * @param {boolean} loading if true or undefined it is loading
   */
  private fetchingInitialInfo(loading?: boolean) {
    if (loading == null) {
      loading = true;
    }
    this.equipmentDataLoading = loading;
  }

  /**
   * Whether equipment data has been altered
   * @return {boolean}
   */
  public isEquipmentChanged(): boolean {
    const currentEquipmentData = JSON.stringify(
      this.newEquipmentFormGroup!.value
    );
    const originalEquipmentData = JSON.stringify(this.originalEquipmentData);
    return currentEquipmentData !== originalEquipmentData;
  }

  /**
   * Cancel equipment edit, going back to original state
   */
  editEquipmentCancelClick() {
    this.removeEquipmentsWithoutSerial();
    const equipment = this.equipmentsArray.at(-1);
    if (this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING') {
      this.businessEquipmentFormPhase = 'LNR_REGISTER';
      equipment.enable();
    } else {
      if (equipment?.get('id')?.value === undefined) {
        this.removeTuple();
      }
      if (this.isEquipmentChanged()) {
        this.updateEquipmentData();
      }
      this.businessEquipmentFormPhase = 'LNR_REGISTER';
      this.isAddingEquipment = false;
    }
  }
  /**
   * Removes all equipments without serial
   */
  removeEquipmentsWithoutSerial() {
    const equipments = this.equipmentsArray.controls;
    for (let i = equipments.length - 1; i >= 0; i--) {
      if (!equipments[i].get('serial')?.value) {
        this.equipmentsArray.removeAt(i);
      }
    }
  }

  /**
   * Removing the last fields index from a form array
   * @param {number} i
   */
  removeTuple(i?: number) {
    let removeIndex = -1;

    if (i != null) {
      removeIndex = i;
    }

    this.equipmentsArray.removeAt(removeIndex);
  }

  /**
   * Next step for adding new equipment
   */
  editEquipmentNextClick() {
    const equipment = RegisterService.createEquipmentFormTuple();
    const serial = this.newEquipmentFormGroup!.get('serial');
    const planModel = this.newEquipmentFormGroup!.get('planModel');
    equipment.get('serial')?.setValue(serial?.getRawValue());
    equipment.get('planModel')?.setValue(planModel?.getRawValue());
    if (!serial) {
      return;
    }

    if (this.shouldCallValidationAPI(this.equipmentsArray, serial)) {
      equipment.disable();

      this.dialogService.openDialog(
        {
          title: this.labels.doYouWishToSaveChanges,
          failButtonText: this.labels.cancel,
          content: this.saveChangesText(),
          successButtonText: this.labels.saveChanges,
          contentKeepWhiteSpace: true,
        },
        () => {
          this.equipmentActionLoading = true;
          if (
            this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING' &&
            serial?.value
          ) {
            return this.epsonRegister(serial, equipment);
          }
          this.addNewEquipment(serial, equipment);
        },
        () => {
          equipment.enable();
        }
      );
    }

    return;
  }

  /**
   * @param { FormArray } equipmentsArray
   * @param { AbtractControl } serial
   * @return {boolean} whether or not validation api should be called
   */
  shouldCallValidationAPI(
    equipmentsArray: FormArray,
    serial: AbstractControl
  ): boolean {
    const hasDuplicate = equipmentsArray.controls.reduce(
      (acc, control) => acc || control.get('serial')?.value === serial.value,
      false
    );
    if (hasDuplicate) {
      serial.setErrors(errorObjectBuilder('duplicate-serial'));
      return false;
    }
    return true;
  }

  /**
   * Adds a new equipment to the business
   * @param {AbstractControl} serial
     @param {AbstractControl} equipment
   */
  addNewEquipment(serial: AbstractControl, equipment: AbstractControl) {
    this.productService
      .validateSerial([serial.value], this.businessSummary.country!.initials)
      .subscribe({
        next: ([validationResponse, ..._]) => {
          switch (validationResponse?.status) {
            case SerialModelValidationStatus.NOT_REGISTERED_IN_SAP:
              this.businessEquipmentFormPhase = 'EPSON_UNREGISTERED_PENDING';
              this.newEquipmentFormGroup!.get('planModel')?.setValue(
                validationResponse.planModel
              );
              this.equipmentActionLoading = false;
              return;
            case SerialModelValidationStatus.NOT_REGISTERED:
              equipment
                .get('planModel')
                ?.setValue(validationResponse.planModel);
              this.saveEquipment(equipment);
              return;
            case SerialModelValidationStatus.ALREADY_REGISTERED:
              equipment.enable();
              serial.setErrors(
                errorObjectBuilder('serial-already-registered-on-epson-rewards')
              );
              this.equipmentActionLoading = false;
              return;
            case SerialModelValidationStatus.MODEL_NOT_FOUND:
              equipment.enable();
              serial.setErrors(errorObjectBuilder('model-not-found'));
              this.dialogService.openDialog({
                title: this.labels.couldNotCheckPrinterSerialData,
                content: this.labels.noCommPrinterDatabase,
                successButtonText: this.labels.ok,
              });
              this.equipmentActionLoading = false;
              return;
            case SerialModelValidationStatus.INVALID_SERIAL:
            default:
              equipment.enable();
              serial.setErrors(errorObjectBuilder('invalid-serial-number'));
              this.equipmentActionLoading = false;
              return;
          }
        },
        error: () => {
          equipment.enable();
          serial.setErrors(errorObjectBuilder('invalid-serial-number'));
          this.equipmentActionLoading = false;
        },
      });
  }

  /**
   * Decides the label for rightmost button in equipment section
   */
  get editEquipmentNextButtonLabel() {
    if (this.isEquipmentsArrayOk && !this.isAddingEquipment) {
      return this.labels.editBusinessEquipment;
    } else if (
      this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING'
    ) {
      return this.labels.saveChanges;
    } else {
      return this.labels.validate;
    }
  }

  /**
   * Easy access for equipments array
   */
  get equipmentsArray() {
    return this.equipmentData.formGroup.controls[
      'equipments'
    ] as UntypedFormArray;
  }

  /**
   * If all equipments are validated and registered
   */
  get isEquipmentsArrayOk() {
    return this.equipmentsArray.controls.reduce(
      (acc, control) =>
        acc && (control.get('id')?.value !== undefined || control.disabled),
      true
    );
  }

  /**
   * Decides which label feedback to use
   */
  get feedbackLabel() {
    let feedbackLabel = '';
    if (this.ephemeralFeedback.feedback === 'eligible') {
      feedbackLabel = 'product-eligible-for-rewards-program';
    } else if (this.ephemeralFeedback.feedback === 'registered') {
      feedbackLabel = 'product-successfully-registered';
    }
    return feedbackLabel;
  }

  /**
   * Whether there is a pending equipment to be registered
   */
  get pendingProductRegistration() {
    return this.businessEquipmentFormPhase === 'EPSON_UNREGISTERED_PENDING';
  }

  /**
   * If equipment section is loading something, either its data or its action
   */
  get isEquipmentLoading() {
    return this.equipmentDataLoading || this.equipmentActionLoading;
  }

  /**
   * New Serial Form getter
   * @return {FormControl}
   */
  get serial() {
    return this.newEquipmentFormGroup?.get('serial') as FormControl;
  }

  /**
   * Triggers epson register api
   * @param {AbstractControl} serial
   * @param {AbstractControl} equipment
   */
  epsonRegister(serial: AbstractControl, equipment: AbstractControl) {
    const country = this.businessSummary?.country?.initials;
    if (!country) {
      return;
    }
    this.registerService.registerEquipmentFormGroup.disable();
    this.equipmentActionLoading = true;
    this.productService
      .epsonSAPEquipmentRegistration(
        fromTRegisterEquipmentFormGroup(
          this.registerService.registerEquipmentFormGroup,
          serial.value,
          country
        )
      )
      .pipe(take(1))
      .subscribe({
        next: (sapResponse) => {
          if (sapResponse.active) {
            this.businessEquipmentFormPhase = 'LNR_REGISTER';
            this.ephemeralFeedback.feedback = 'registered';
            this.saveEquipment(equipment);
          } else {
            this.dialogService.openDialog({
              title: this.labels.couldNotRegisterPrinter,
              content: this.labels.couldNotRegisterPrinter,
              successButtonText: this.labels.ok,
            });
          }
        },
        error: () => {
          this.dialogService.openDialog({
            title: this.labels.couldNotRegisterPrinter,
            content: this.labels.noCommPrinterDatabase,
            successButtonText: this.labels.ok,
          });
        },
      })
      .add(() => {
        this.equipmentActionLoading = false;
        this.registerService.registerEquipmentFormGroup.enable();
      });
  }

  /**
   * Checks if button should be disabled when business is inactive
   * @return {boolean}
   */
  checkDisableAddEquipment(): boolean {
    return (
      this.businessSummary.status === BusinessStatus.DISABLED &&
      this.authenticatedUserService.isVOAdmin()
    );
  }

  /**
   * Will display at most two lists in human readable way.
   * One lists equipments that will be added to this account,
   * and the other is of equipments that will be removed from this account.
   * @return {string}
   */
  saveChangesText() {
    const equipmentsToAdd = [this.newEquipmentFormGroup!.getRawValue()];

    const equipmentsBeingAdded =
      equipmentsToAdd.length > 0
        ? `${this.labels.equipmentsBeingADDED}:\n`
        : '';

    return `${equipmentsBeingAdded}${equipmentsToAdd
      .map((equipment: any) => '  - ' + equipment.serial)
      .join('\n')}`;
  }

  /**
   *
   *
   */
  private async getSecondaryContent(): Promise<SecondaryContent[]> {
    return [
      {
        content: await this.translationService.translateString(
          this.labels.disableBusinessWarning
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          this.labels.activateBusinessInfoPrinter
        ),
        style: {
          color: 'var(--Cinzas-300, #4E4E4E)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: '125%',
        },
      },
    ];
  }
}
